import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import ResponsiveAdSection from "../../Components/ResponsiveAdSection/ResponsiveAdSection";
import API from "../../config/api";
import { AD_SLOTS } from "../../config/adSlots";
import { useHomeData } from "../../context/HomeContext";
import useSeo from "../../hooks/useSeo";
import { useTranslation } from "../../i18n/LanguageProvider";
import { getInitialData } from "../../utils/initialData";
import { buildNewsSeo, getStaticSeoForPath } from "../../utils/seo";
import { getIdFromSlug } from "../../utils/slug";
import Minigame from "../Home/Components/Minigame/Minigame";
import Title from "../Home/Components/Title/Title";
import LatestNewsSidebar from "./Components/LatestNewsSidebar";
import NewsArticle from "./Components/NewsArticle";
import NewsBreadcrumb from "./Components/NewsBreadcrumb";
import NewsRemarkableWomenSection from "./Components/NewsRemarkableWomenSection";
import "./News.sass";

const LATEST_NEWS_LIMIT = 3;
const REMARKABLE_WOMEN_LIMIT = 4;

// News item the server already rendered for this URL.
function getInitialNews(slug) {
    const initial = getInitialData("newsDetail");

    return initial?.item && Number(initial.item.id) === getIdFromSlug(slug) ? initial : null;
}

function NewsDetailPage() {
    const { slug } = useParams();
    const { language, t } = useTranslation();
    const { topWomen, initialGameData } = useHomeData();
    const [news, setNews] = useState(() => getInitialNews(slug)?.item || null);
    const [latestNews, setLatestNews] = useState(() => getInitialNews(slug)?.latestItems || []);
    const [loading, setLoading] = useState(() => !getInitialNews(slug));
    const [latestLoading, setLatestLoading] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const seo = useMemo(() => {
        if (news) return buildNewsSeo(news, language);
        if (notFound) {
            return {
                ...getStaticSeoForPath("/noutati", language),
                robots: "noindex, follow",
            };
        }

        return getStaticSeoForPath("/noutati", language);
    }, [language, news, notFound]);
    const remarkableWomen = Array.isArray(topWomen) ? topWomen.slice(0, REMARKABLE_WOMEN_LIMIT) : [];

    useSeo(seo);

    useEffect(() => {
        window.scrollTo(0, 0);
        setNotFound(false);

        const initialNews = getInitialNews(slug);

        if (initialNews) {
            setNews(initialNews.item);
            setLoading(false);
            return;
        }

        setLoading(true);
        setNews(null);
        setLatestNews([]);

        fetch(`${API.getNews}?slug=${encodeURIComponent(slug || "")}&lang=${language}`, {
            credentials: "include",
        })
            .then((res) => res.json())
            .then((data) => {
                if (data?.data) {
                    setNews(data.data);
                } else {
                    setNotFound(true);
                }
            })
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [language, slug]);

    useEffect(() => {
        const initialNews = getInitialNews(slug);

        if (initialNews?.latestItems?.length > 0) {
            setLatestNews(initialNews.latestItems.slice(0, LATEST_NEWS_LIMIT));
            setLatestLoading(false);
            return undefined;
        }

        let cancelled = false;
        setLatestLoading(true);

        fetch(`${API.getNews}?limit=${LATEST_NEWS_LIMIT}&lang=${language}`, {
            credentials: "include",
        })
            .then((res) => res.json())
            .then((data) => {
                if (cancelled) return;
                setLatestNews((data?.data || []).slice(0, LATEST_NEWS_LIMIT));
            })
            .catch(() => {
                if (!cancelled) {
                    setLatestNews([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLatestLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [language, slug]);

    if (loading) {
        return (
            <main className="news-detail general-wrapper">
                <p className="news__loading">{t("news.loading")}</p>
            </main>
        );
    }

    if (notFound || !news) {
        return (
            <main className="news-detail general-wrapper">
                <NewsBreadcrumb />
                <article className="news-detail__article card-common">
                    <h1 className="news-detail__title">{t("news.notFound")}</h1>
                </article>
            </main>
        );
    }

    const paragraphs = String(news.content || "")
        .split(/\n{2,}/)
        .map((paragraph) => paragraph.trim())
        .filter(Boolean);

    return (
        <main className="news-detail general-wrapper">
            <NewsBreadcrumb title={news.title} />
            <div className="news-detail__layout">
                <div className="news-detail__primary">
                    <NewsArticle news={news} paragraphs={paragraphs} />
                </div>
                <LatestNewsSidebar news={latestNews} loading={latestLoading} />
            </div>

            <ResponsiveAdSection
                adSlot={AD_SLOTS.displayInline}
                className="news-detail__ad news-detail__ad--after-news"
            />

            <section className="news-detail__minigame" aria-labelledby="news-minigame-title">
                <Title id="news-minigame-title" text={t("home.recognize")} />
                <Minigame initialGameData={initialGameData} />
            </section>

            <ResponsiveAdSection
                adSlot={AD_SLOTS.displayInline}
                className="news-detail__ad news-detail__ad--after-minigame"
            />

            <NewsRemarkableWomenSection women={remarkableWomen} />
        </main>
    );
}

export default NewsDetailPage;
